import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** The surface tiers of docs/frontend-design-conventions.md §1. */
export type CardTier = 'card' | 'hero' | 'inset' | 'muted';

const TIERS: Record<CardTier, string> = {
  card: 'rounded-2xl border bg-card/80 backdrop-blur-sm shadow-sm',
  hero: 'rounded-2xl border bg-card/80 backdrop-blur-sm shadow-md',
  inset: 'rounded-xl border bg-card/60',
  muted: 'rounded-xl bg-muted/60',
};

const PADDING = { none: '', sm: 'p-4', md: 'p-5', lg: 'p-6' } as const;

export type CardProps<T extends ElementType = 'div'> = {
  as?: T;
  tier?: CardTier;
  padding?: keyof typeof PADDING;
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithRef<T>, 'as' | 'className' | 'children'>;

/** A page surface (React 19: `ref` is passed through like any prop): `card` (default) for content, `hero` for page identity, `inset` inside a card, `muted` for secondary blocks. */
export function Card<T extends ElementType = 'div'>({
  as,
  tier = 'card',
  padding = 'md',
  className,
  children,
  ...props
}: CardProps<T>) {
  const Tag: ElementType = as ?? 'div';
  return (
    <Tag className={cn(TIERS[tier], PADDING[padding], className)} {...props}>
      {children}
    </Tag>
  );
}
